import { useState } from 'react';
import { XIcon, AlertTriangleIcon } from 'lucide-react';
interface SuspendEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  onConfirm: (reason: string) => void;
}
export function SuspendEmployeeModal({
  isOpen,
  onClose,
  employeeName,
  onConfirm
}: SuspendEmployeeModalProps) {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  if (!isOpen) return null;
  const isValid = reason.length >= 10 && acknowledged;
  const handleConfirm = () => {
    if (isValid) {
      onConfirm(reason);
      setReason('');
      setAcknowledged(false);
      onClose();
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose} />
      
      <div className="relative w-full max-w-[600px] bg-surface-light rounded-[12px] shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-border bg-warning-background/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning-background flex items-center justify-center">
              <AlertTriangleIcon className="w-5 h-5 text-coral" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold text-iron-grey">
                Suspend Employee
              </h2>
              <p className="font-body text-sm text-text-secondary mt-0.5">
                {employeeName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-iron-grey transition-colors">
            
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div className="bg-warning-background border-l-4 border-coral rounded-r-[8px] p-4">
            <h3 className="font-body text-sm font-semibold text-iron-grey mb-2">
              Warning: This will suspend the employee
            </h3>
            <p className="font-body text-sm text-text-secondary">
              Suspending this employee will change their status to "On Leave"
              and they will not appear in active payroll processing. You can
              reactivate them later if needed.
            </p>
          </div>

          <div>
            <label className="block font-body text-sm font-medium text-iron-grey mb-1.5">
              Reason for Suspension <span className="text-strawberry">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Explain why this employee is being suspended..."
              className="w-full px-3 py-2 bg-surface-light border border-border rounded-[6px] font-body text-sm text-iron-grey focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary resize-none" />
            
            <p className="font-body text-xs text-text-secondary mt-1">
              {reason.length}/∞ characters (minimum 10 required)
            </p>
          </div>

          <label className="flex items-start gap-3 p-4 bg-muted rounded-[8px] cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 rounded border-border text-primary focus:ring-primary w-4 h-4 cursor-pointer" />
            
            <span className="font-body text-sm text-iron-grey">
              I understand this employee will be marked as "On Leave" and
              excluded from active payroll
            </span>
          </label>
        </div>

        <div className="p-5 border-t border-border bg-background-light flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 font-body text-sm font-medium text-iron-grey bg-surface-light border border-border rounded-[6px] hover:bg-muted transition-colors">
            
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={handleConfirm}
            className="px-4 py-2 font-body text-sm font-medium text-white bg-coral rounded-[6px] hover:bg-strawberry transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
            
            Suspend Employee
          </button>
        </div>
      </div>
    </div>);

}
